import React, { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Camera, Trash2, ShieldCheck, Bell, KeyRound, Clock, LogIn, LogOut, UserCog, Eye, EyeOff } from "lucide-react";
import { useAuth } from "../lib/auth";
import { api, avatarUrl, errorMessage } from "../lib/api";
import type { ActivityEntry } from "../lib/types";
import { PageHeader, Card } from "../components/core/Layout";
import { Button } from "../components/core/Button";
import { Field, Input } from "../components/core/Field";
import * as SwitchPrimitive from "@radix-ui/react-switch";
import { cn } from "../lib/utils";

const fmt = (iso?: string | null) => (iso ? new Date(iso).toLocaleString("en-MY", { dateStyle: "medium", timeStyle: "short" }) : "—");

function AvatarCard() {
  const { user, tenant, refresh } = useAuth();
  const fileRef = useRef<HTMLInputElement>(null);
  const isBO = user?.user_type === "super_admin";
  const initials = (user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  const upload = useMutation({
    mutationFn: async (file: File) => { const fd = new FormData(); fd.append("file", file); return (await api.post("/auth/avatar", fd)).data; },
    onSuccess: async () => { await refresh(); toast.success("Profile photo updated"); },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const remove = useMutation({
    mutationFn: async () => (await api.delete("/auth/avatar")).data,
    onSuccess: async () => { await refresh(); toast.success("Profile photo removed"); },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <Card testId="profile-avatar-card" className="rise">
      <div className="flex flex-col items-center text-center">
        <div className="relative">
          {user?.avatar_file_id ? (
            <img src={avatarUrl(user.id, user.avatar_file_id)} alt={user.name} className="h-28 w-28 rounded-full object-cover ring-4 ring-slate-100" data-testid="profile-avatar-image" />
          ) : (
            <div className="flex h-28 w-28 items-center justify-center rounded-full bg-[color:var(--brand)] font-display text-3xl font-bold text-white ring-4 ring-slate-100" data-testid="profile-avatar-initials">{initials}</div>
          )}
          <button data-testid="avatar-upload-button" onClick={() => fileRef.current?.click()} disabled={upload.isPending}
            className="absolute -bottom-1 -right-1 flex h-9 w-9 items-center justify-center rounded-full bg-[color:var(--gold)] text-slate-900 shadow-md transition-transform hover:scale-105 disabled:opacity-50">
            <Camera className="h-4 w-4" />
          </button>
          <input ref={fileRef} data-testid="avatar-file-input" type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) upload.mutate(f); e.target.value = ""; }} />
        </div>
        <h2 className="mt-4 font-display text-lg font-bold text-slate-900" data-testid="profile-display-name">{user?.name}</h2>
        <p className="text-sm text-slate-500" data-testid="profile-email">{user?.email}</p>
        {user?.job_title && <p className="mt-0.5 text-xs text-slate-500" data-testid="profile-job-title">{user.job_title}</p>}
        <span className="mt-3 rounded-full bg-indigo-50 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-[color:var(--brand)]" data-testid="profile-role-badge">
          {isBO ? "POMEN Super Admin" : "Client Administrator"}
        </span>
        {!isBO && <p className="mt-2 text-xs text-slate-500" data-testid="profile-tenant">{tenant?.branding?.display_name || tenant?.name}</p>}
        {user?.avatar_file_id && (
          <button data-testid="avatar-remove-button" onClick={() => remove.mutate()} className="mt-3 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-rose-600"><Trash2 className="h-3 w-3" />Remove photo</button>
        )}
      </div>
      <dl className="mt-6 space-y-2 border-t border-slate-100 pt-4 text-sm">
        <div className="flex justify-between"><dt className="text-slate-500">Member since</dt><dd className="font-medium text-slate-800" data-testid="profile-member-since">{fmt(user?.created_at)}</dd></div>
        <div className="flex justify-between"><dt className="text-slate-500">Last sign-in</dt><dd className="font-medium text-slate-800" data-testid="profile-last-login">{fmt(user?.last_login_at)}</dd></div>
        <div className="flex justify-between"><dt className="text-slate-500">Password changed</dt><dd className="font-medium text-slate-800" data-testid="profile-password-changed">{user?.password_changed_at ? fmt(user.password_changed_at) : "Never"}</dd></div>
      </dl>
    </Card>
  );
}

function DetailsForm() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({ name: user?.name || "", phone: user?.phone || "", job_title: user?.job_title || "" });
  const save = useMutation({
    mutationFn: async () => (await api.patch("/auth/profile", form)).data,
    onSuccess: async () => { await refresh(); toast.success("Profile saved"); },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const dirty = form.name !== (user?.name || "") || form.phone !== (user?.phone || "") || form.job_title !== (user?.job_title || "");
  return (
    <Card title="Personal Details" icon={UserCog} testId="profile-details-card" className="rise rise-1">
      <form className="grid gap-4 sm:grid-cols-2" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        <Field label="Full name" className="sm:col-span-2"><Input data-testid="profile-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required minLength={2} /></Field>
        <Field label="Email" hint="Email is managed by your administrator."><Input data-testid="profile-email-input" value={user?.email || ""} disabled className="bg-slate-50 text-slate-500" /></Field>
        <Field label="Phone"><Input data-testid="profile-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+60 12-345 6789" /></Field>
        <Field label="Job title" className="sm:col-span-2"><Input data-testid="profile-job-title-input" value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} placeholder="e.g. Fleet Manager" /></Field>
        <div className="flex justify-end sm:col-span-2"><Button type="submit" data-testid="profile-save-button" loading={save.isPending} disabled={!dirty}>Save changes</Button></div>
      </form>
    </Card>
  );
}

const rules = [
  { label: "8+ characters", test: (p: string) => p.length >= 8 },
  { label: "Uppercase letter", test: (p: string) => /[A-Z]/.test(p) },
  { label: "Lowercase letter", test: (p: string) => /[a-z]/.test(p) },
  { label: "Number", test: (p: string) => /\d/.test(p) },
];

function PasswordForm() {
  const [f, setF] = useState({ current_password: "", new_password: "", confirm: "" });
  const [show, setShow] = useState(false);
  const passed = rules.filter((r) => r.test(f.new_password)).length;
  const match = f.confirm.length > 0 && f.new_password === f.confirm;
  const ready = passed === rules.length && match && f.current_password.length > 0;
  const change = useMutation({
    mutationFn: async () => (await api.post("/auth/change-password", { current_password: f.current_password, new_password: f.new_password })).data,
    onSuccess: () => { toast.success("Password changed successfully"); setF({ current_password: "", new_password: "", confirm: "" }); },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const type = show ? "text" : "password";
  return (
    <Card title="Change Password" icon={KeyRound} testId="profile-password-card" className="rise rise-2">
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); change.mutate(); }}>
        <Field label="Current password"><Input data-testid="current-password-input" type={type} value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} autoComplete="current-password" required /></Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="New password"><Input data-testid="new-password-input" type={type} value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} autoComplete="new-password" required /></Field>
          <Field label="Confirm new password"><Input data-testid="confirm-password-input" type={type} value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} autoComplete="new-password" required className={cn(f.confirm && !match && "border-rose-400")} /></Field>
        </div>
        <div className="flex h-1.5 gap-1" data-testid="password-strength-meter">
          {rules.map((_, i) => <span key={i} className={cn("flex-1 rounded-full transition-colors", i < passed ? (passed === rules.length ? "bg-emerald-500" : "bg-amber-400") : "bg-slate-200")} />)}
        </div>
        <ul className="grid grid-cols-2 gap-1 text-xs sm:grid-cols-4">
          {rules.map((r) => <li key={r.label} className={cn("flex items-center gap-1", r.test(f.new_password) ? "text-emerald-600" : "text-slate-400")}><ShieldCheck className="h-3 w-3" />{r.label}</li>)}
        </ul>
        {f.confirm && !match && <p className="text-xs text-rose-600" data-testid="password-mismatch-error">Passwords do not match.</p>}
        <div className="flex items-center justify-between">
          <button type="button" data-testid="toggle-password-visibility" onClick={() => setShow(!show)} className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800">{show ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}{show ? "Hide" : "Show"} passwords</button>
          <Button type="submit" data-testid="change-password-button" loading={change.isPending} disabled={!ready}>Update password</Button>
        </div>
      </form>
    </Card>
  );
}

function PreferencesCard() {
  const { user, refresh } = useAuth();
  const prefs = user?.preferences || { email_alerts: true, weekly_digest: false };
  const save = useMutation({
    mutationFn: async (next: typeof prefs) => (await api.put("/auth/preferences", next)).data,
    onSuccess: async () => { await refresh(); toast.success("Preferences saved"); },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const Row = ({ id, label, hint, value }: { id: keyof typeof prefs; label: string; hint: string; value: boolean }) => (
    <div className="flex items-center justify-between gap-4 py-3">
      <div><p className="text-sm font-medium text-slate-800">{label}</p><p className="text-xs text-slate-500">{hint}</p></div>
      <SwitchPrimitive.Root data-testid={`pref-${id.replace("_", "-")}-switch`} checked={value} disabled={save.isPending} onCheckedChange={(v: boolean) => save.mutate({ ...prefs, [id]: v })}
        className="relative inline-flex h-6 w-11 shrink-0 items-center rounded-full border-2 border-transparent transition-colors data-[state=checked]:bg-[color:var(--brand)] data-[state=unchecked]:bg-slate-300 disabled:opacity-50">
        <SwitchPrimitive.Thumb className="block h-5 w-5 rounded-full bg-white shadow-sm transition-transform data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0" />
      </SwitchPrimitive.Root>
    </div>
  );
  return (
    <Card title="Notifications" icon={Bell} testId="profile-preferences-card" className="rise rise-3">
      <div className="divide-y divide-slate-100">
        <Row id="email_alerts" label="Compliance alert emails" hint="Receive an email when road tax, insurance or PUSPAKOM is expiring." value={prefs.email_alerts} />
        <Row id="weekly_digest" label="Weekly fleet digest" hint="A Monday summary of compliance health and open alerts." value={prefs.weekly_digest} />
      </div>
    </Card>
  );
}

const icons: Record<string, typeof LogIn> = { "auth.login": LogIn, "auth.logout": LogOut, "auth.password_changed": KeyRound };

function ActivityCard() {
  const { data } = useQuery<ActivityEntry[]>({ queryKey: ["my-activity"], queryFn: async () => (await api.get("/auth/activity", { params: { limit: 12 } })).data });
  return (
    <Card title="Recent Account Activity" icon={Clock} testId="profile-activity-card" className="rise rise-4">
      {!data?.length ? <p className="text-sm text-slate-500">No recent activity.</p> : (
        <ol className="space-y-3">
          {data.map((a) => { const Icon = icons[a.action] || UserCog; return (
            <li key={a.id} className="flex items-start gap-3 text-sm" data-testid="activity-row">
              <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-slate-100 text-slate-600"><Icon className="h-3.5 w-3.5" /></span>
              <div className="min-w-0 flex-1"><p className="truncate text-slate-800">{a.summary}</p><p className="text-xs text-slate-400">{fmt(a.created_at)}</p></div>
            </li>
          ); })}
        </ol>
      )}
    </Card>
  );
}

export default function ProfilePage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  React.useEffect(() => { qc.invalidateQueries({ queryKey: ["my-activity"] }); }, [user, qc]);
  return (
    <div data-testid="profile-page">
      <PageHeader eyebrow="Account" title="My Profile" subtitle="Manage your personal details, security and notification preferences." />
      <div className="grid gap-6 lg:grid-cols-[320px_minmax(0,1fr)]">
        <div className="min-w-0 space-y-6"><AvatarCard /><ActivityCard /></div>
        <div className="min-w-0 space-y-6"><DetailsForm key={user?.id} /><PasswordForm /><PreferencesCard /></div>
      </div>
    </div>
  );
}
